// External imports
import React, { useEffect } from 'react';
import { Divider, Button, Paper, Snackbar } from '@material-ui/core';
// Internal imports
import WebHelpers from '~/app/webHelpers.js';
import XlinkAPI from '~/app/api/xlinkAPI';
import ErrorHelpers from '~/app/errorHelpers.js';
import SimpleDialog from '#/Common/SimpleDialog.jsx';
import { EXTENDED_SETUP_PAGES } from '~/app/constants.js';
import {
  INDIVIDUAL_RETURN_PRINT_ORDER_DEFUALT,
  BUSINESS_RETURN_PRINT_ORDER_DEFUALT,
  SIG_BLOCK_TWO_VALUE_MAP,
} from './Helper/printSetupConstants.js';
import AccessControl from '../../Auth/AccessControl.jsx';
import { useSetState } from '~/app/Utility/customHooks';
import ReturnPrinting from '~/app/Components/NewAccount/SingleOffice/PrintSetupComponents/returnPrinting.jsx';
import PrintOptions from '~/app/Components/NewAccount/SingleOffice/PrintSetupComponents/printOptions.jsx';
import PrinterSetting from '~/app/Components/NewAccount/SingleOffice/PrintSetupComponents/printerSetting.jsx';
import { canAccessCorp } from '~/app/Utility/general';
import { NISTAuditLog } from '~/app/Utility/clientNISTLogger.js';
import { NIST_LOG_EVENTS } from '~/app/Utility/clientNISTConstants.js';
// Styles imports
import '~/app/Pages/Setup/css/setup.css';
import '~/app/Components/NewAccount/SingleOffice/css/printingSetup.css';

const PrintingSetup = props => {
  const payload = WebHelpers.getJWTPayload();
  const [state, setState] = useSetState({
    confirmCancel: false,
    gotoPrev: false,
    snackbarOpen: false,
    expandedPrinterSettings: true,
    expandedPrintOptions: true,
    expandedReturnPrinting: true,
    expandedBusinesPrinting: true,
  });

  useEffect(() => {
    getInitialConfigState();
  }, []);

  const getInitialConfigState = () => {
    window.scrollTo(0, 0);
    XlinkAPI.getPrintingSetup(props.loginID)
      .then(res => {
        const data = res.data;
        setConfigState(data);
      })
      .catch(err => {
        ErrorHelpers.handleError('Failed to get Printing Setup', err);
      });
  };

  // Handle onChange without validation for selec/menuitem fields
  const handleChange = event => {
    setState({
      fieldMap: {
        ...state.fieldMap,
        [event.target.name]: {
          ...state.fieldMap[event.target.name],
          configValue: event.target.value[0],
        },
      },
    });
  };

  // Handle onChange specifically for the second signature block dropdown (Blank value needs to be set to '', not a single character)
  const handleSignatureBlockValueChange = event => {
    const { name, value } = event.target;

    setState({
      fieldMap: {
        ...state.fieldMap,
        [name]: {
          ...state.fieldMap[name],
          configValue: SIG_BLOCK_TWO_VALUE_MAP[value],
        },
      },
    });
  };

  // Handle onChange without validation fon input fields
  const handleInputChange = (event, dollarValue = false) => {
    let value = event.target.value.toUpperCase();
    if (dollarValue) {
      // remove preceding $, 0's and commas before decimal point
      value = event.target.value.replace(/[$0,]+/, '');

      // finally remove decimal
      value = value.replace(/[.]/g, '');
    }
    setState({
      fieldMap: {
        ...state.fieldMap,
        [event.target.name]: {
          ...state.fieldMap[event.target.name],
          configValue: value,
        },
      },
    });
  };

  // Handle onChange without validation for checkbox
  const handleCheckbox = event => {
    setState({
      fieldMap: {
        ...state.fieldMap,
        [event.target.name]: {
          ...state.fieldMap[event.target.name],
          configValue: event.target.checked ? '1' : '0',
        },
      },
    });
  };

  // TODO: Page needs to be built dynamically, this is not maintainable
  const setConfigState = data => {
    const fieldList = Object.getOwnPropertyNames(data.config);
    let fieldMap = {};
    for (let i = 0; i < fieldList.length; i++) {
      fieldMap = {
        ...fieldMap,
        [fieldList[i]]: {
          configID: data.config[fieldList[i]]?.configID,
          configValue: data.config[fieldList[i]]?.configValue,
        },
      };
    }

    // check if first print order global is empty, if it is we need to set default order values
    if (!fieldMap.PO01?.configValue) {
      setDefaultPrintOrder('PO', INDIVIDUAL_RETURN_PRINT_ORDER_DEFUALT, fieldMap);
    } else {
      repairPrintOrder('PO', INDIVIDUAL_RETURN_PRINT_ORDER_DEFUALT, fieldMap);
    }

    if (!fieldMap.CP01?.configValue) {
      setDefaultPrintOrder('CP', BUSINESS_RETURN_PRINT_ORDER_DEFUALT, fieldMap);
    } else {
      repairPrintOrder('CP', BUSINESS_RETURN_PRINT_ORDER_DEFUALT, fieldMap);
    }

    setState({
      fieldMap,
    });
  };

  // Reconciles a stored print order against the canonical list.
  //
  // An order saved before the default list carried every entry can be missing
  // an item and hold a stale duplicate in the trailing slot. calcserver
  // dispatches each slot by exact name, so a missing entry means that section
  // is never scheduled and a duplicate schedules the same section twice.
  //
  // Entries the canonical list does not contain are dropped, since calcserver
  // cannot dispatch them, as are repeats after the first. Missing entries are
  // inserted at their canonical position. Any customized ordering that survives
  // those rules is preserved. The repaired order is persisted the next time the
  // page is saved.
  const repairPrintOrder = (globalPrefix, canonicalOrder, fieldMap) => {
    const slotPattern = new RegExp(`^${globalPrefix}\\d{2}$`);
    const slotKeys = Object.keys(fieldMap)
      .filter(key => slotPattern.test(key))
      .sort();

    if (slotKeys.length === 0) {
      return;
    }

    const placed = new Set();
    const order = [];

    slotKeys.forEach(key => {
      const value = fieldMap[key]?.configValue;
      if (value && canonicalOrder.includes(value) && !placed.has(value)) {
        placed.add(value);
        order.push(value);
      }
    });

    canonicalOrder.forEach((entry, canonicalIdx) => {
      if (!placed.has(entry)) {
        placed.add(entry);
        order.splice(Math.min(canonicalIdx, order.length), 0, entry);
      }
    });

    slotKeys.forEach((key, idx) => {
      if (fieldMap[key]) {
        fieldMap[key].configValue = order[idx] ?? '';
      }
    });
  };

  // alters fieldMap array by adjusting print order globals to default values
  const setDefaultPrintOrder = (globalPrefix, valueArray, fieldMap) => {
    for (let i = 0; i < valueArray.length; i++) {
      const adjustedIdx = i + 1;
      const keyIndex = adjustedIdx < 10 ? '0' + adjustedIdx : adjustedIdx;
      const globalKey = globalPrefix + keyIndex;
      // Skip slots the configuration response did not return. Assigning into a
      // missing key throws, and a fabricated entry would carry no configID, so
      // submitPrinterSetupData would drop it before saving anyway.
      if (fieldMap[globalKey]) {
        fieldMap[globalKey].configValue = valueArray[i];
      }
    }
  };

  // general function for updating field map with multiple values at once
  const updateFieldMap = dataObject => {
    setState({
      fieldMap: {
        ...state.fieldMap,
        ...dataObject,
      },
    });
  };

  // Add functin to pass all values through API call
  const submitPrinterSetupData = (gotoNext, printOrder = null) => {
    // convert fieldMap to array
    let fieldMap = state.fieldMap;
    if (printOrder) {
      fieldMap = { ...fieldMap, ...printOrder };
    }

    const fieldMapArray = Object.values(fieldMap);
    // Clean up any disabled values
    const newPrinterData = fieldMapArray.filter(obj => Number.isInteger(obj?.configID));

    newPrinterData.forEach(function (element, index, arr) {
      element.configValue = element.configValue.replace(/[$.,]/g, '');
      arr[index] = element;
    });

    XlinkAPI.updatePrintingSetup(newPrinterData, props.loginID)
      .then(() => {
        if (gotoNext) {
          props.loadSetupComponent(EXTENDED_SETUP_PAGES.CHECKS.NAME);
        } else {
          toggleState('snackbarOpen', true);
        }

        // NIST - logging the user action of updating Printing setup
        NISTAuditLog(NIST_LOG_EVENTS.UPDATE_PRINTING_SETUP.key);
      })
      .catch(err => {
        ErrorHelpers.handleError('Print Setup Error', err);
      });
  };

  // toggle a state, given the toggle name
  // if value is given change to that value, otherwise just switch to !state
  const toggleState = (toggleName, value) => {
    if (value) {
      setState({
        [toggleName]: value,
      });
    } else {
      setState({
        [toggleName]: !state[toggleName],
      });
    }
  };

  return (
    <div className="container-fixed" style={{ marginTop: '2.5rem' }}>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={state.snackbarOpen}
        autoHideDuration={2500}
        onClose={() => toggleState('snackbarOpen', false)}
        id="snkbarconfsaved"
        message={<span id="success-message">Configuration Saved</span>}
      />
      <AccessControl requiredAction="read" accessLevel="edit_print_config" disableOnFalse={true}>
        <Paper>
          <form
            style={{
              marginLeft: '3vw',
              marginRight: '3vw',
              marginTop: '1vh',
            }}
            onSubmit={e => {
              e.preventDefault();
            }}
          >
            <AccessControl
              requiredAction="write"
              accessLevel="edit_print_config"
              disableOnFalse={true}
            >
              <PrinterSetting
                fieldMap={state.fieldMap}
                handleInputChange={handleInputChange}
                handleExpansion={() => toggleState('expandedReturnPrinting')}
                expanded={state.expandedPrinterSettings}
              />
              <br />
              <Divider />
              <span>
                <ReturnPrinting
                  fieldMap={state.fieldMap}
                  handleCheckbox={handleCheckbox}
                  handleExpansion={() => toggleState('expandedReturnPrinting')}
                  expanded={state.expandedReturnPrinting}
                  isBusiness={false}
                  updateFieldMap={updateFieldMap}
                  saveOrder={submitPrinterSetupData}
                />
                {canAccessCorp(
                  payload,
                  props.officeProfile,
                  props.currentView,
                  props.activeAccessLevels,
                ) ? (
                  <>
                    <br />
                    <Divider />
                    <span>
                      <ReturnPrinting
                        fieldMap={state.fieldMap}
                        handleCheckbox={handleCheckbox}
                        handleExpansion={() => toggleState('expandedBusinesPrinting')}
                        expanded={state.expandedBusinesPrinting}
                        isBusiness={true}
                        updateFieldMap={updateFieldMap}
                        saveOrder={submitPrinterSetupData}
                      />
                    </span>
                  </>
                ) : null}
                <br />
                <Divider />
                <span>
                  <PrintOptions
                    fieldMap={state.fieldMap}
                    handleCheckbox={handleCheckbox}
                    handleChange={handleChange}
                    handleSignatureBlockValueChange={handleSignatureBlockValueChange}
                    handleExpansion={() => toggleState('expandedPrintOptions')}
                    handleInputChange={handleInputChange}
                    expanded={state.expandedPrintOptions}
                    sigBlockOne={state.fieldMap?.SBLW?.configValue}
                    sigBlockTwo={state.fieldMap?.SBLK?.configValue}
                    couponTypeValue={state.fieldMap?.RFTP?.configValue}
                  />
                </span>
              </span>
              <br />
            </AccessControl>
            <Divider />
            <div className="setup-nav-btns">
              <Button
                id="btnPreviousPrintingSetup"
                color="primary"
                onClick={() => {
                  setState({ gotoPrev: true });
                  toggleState('confirmCancel', true);
                }}
              >
                Previous (Cancel)
              </Button>
              <span>
                <Button
                  id="btnCancelPrintingSetup"
                  color="primary"
                  onClick={() => {
                    setState({ gotoPrev: false });
                    toggleState('confirmCancel', true);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  id="btnSavePrintingSetup"
                  color="primary"
                  onClick={() => submitPrinterSetupData(false)}
                >
                  Save
                </Button>
              </span>
              <Button
                id="btnNextPrintingSetup"
                color="primary"
                onClick={() => submitPrinterSetupData(true)}
              >
                Save &amp; Next
              </Button>
            </div>
            <br />
          </form>
        </Paper>
      </AccessControl>
      <SimpleDialog
        open={state.confirmCancel}
        onClose={() => toggleState('confirmCancel', false)}
        onConfirm={() => {
          toggleState('confirmCancel', false);
          if (state.gotoPrev) {
            props.loadSetupComponent(EXTENDED_SETUP_PAGES.EFILING.NAME);
          } else {
            getInitialConfigState();
          }
        }}
        dialogTitle={'Lose Unsaved Changes?'}
        contentText={'Are you sure you want to undo any pending changes?'}
      />
    </div>
  );
};

export default PrintingSetup;
